import { callbackWhenInView, initComponent } from "../../utilities/common";

const settings = {
  th: 200,
  animated: true,
  ani: "data-src-animated",
  ret: "data-src-retina",
  src: "data-src",
  srcset: "data-srcset"
};

const retina = window.devicePixelRatio > 1;
const attrib = retina ? settings.ret : settings.src;

// Helper function to extract media dimensions from media query string
const extractMediaDimensions = (mediaQuery) => {
  const parts = mediaQuery.split(", ");
  const width = parts[0].slice(12, -3);
  const height = parts[1].slice(13, -3);
  return { width, height };
};

// Helper function to convert array-like to array
const toArray = (arrayLike) => [].slice.call(arrayLike, 0);

export const unveil = function (image) {
  const node = image.tagName;
  let source = image.getAttribute(attrib);
  const srcset = image.getAttribute(settings.srcset);

  // Fallback to other source attributes
  source =
    source ||
    image.getAttribute(settings.src) ||
    image.getAttribute(settings.ret);

  if (settings.animated === true) {
    source = image.getAttribute(settings.ani) || source;
  }

  if (source || node === "PICTURE") {
    const parent = image.parentElement;
    const parentWidth = parent.offsetWidth;
    const parentHeight = parent.offsetHeight;
    const upscale = Math.max(
      parentWidth > 100 ? 1.25 : 1.75,
      window.devicePixelRatio * 0.75 // if on a retina device, use their pixel ratio, but doesn't need to be full size so reduce it a little since we will also go one size larger below
    );

    if (node === "IMG") {
      image.setAttribute("src", source);
      if (srcset) {
        image.setAttribute("srcset", srcset);
      }
    } else if (node === "PICTURE") {
      const img = image.querySelector("img");
      const imgSrc =
        img.getAttribute(attrib) ||
        img.getAttribute(settings.src) ||
        img.getAttribute(settings.ret);
      const sources = image.querySelectorAll("source");
      let doOneMore = false;

      // Collect sources to activate first, then set all srcsets at once to prevent race conditions
      const sourcesToActivate = [];

      // we need to reverse the order of the sources since they come into the DOM from largest to smallest but we need to show one larger than the smallest one that meets the mediaWidth and so need to know which was the last smaller one that fit to go one more
      toArray(sources)
        .reverse()
        .forEach(function (e) {
          const media = e.getAttribute("media");
          const { width: mediaWidth, height: mediaHeight } =
            extractMediaDimensions(media);
          const bigEnough =
            parentWidth * upscale >= mediaWidth ||
            parentHeight * upscale >= mediaHeight;
          if (bigEnough || doOneMore) {
            // allow the source if it is bigEnough or if it is one larger that the last one that fit. If the target size was 500 but the image sizes were 480 and 640, then 480 would be included and 600 wouldn't be, then the 480 would get stretched. Allowing one larger than the target Size covers that situation
            sourcesToActivate.push(e);
            doOneMore = bigEnough;
          }
        });

      // If no sources matched, activate all sources of the smallest size (both AVIF and WebP)
      if (sourcesToActivate.length === 0 && sources.length > 0) {
        // Find the smallest size from all sources
        let minSize = Infinity;
        toArray(sources).forEach(function (e) {
          const media = e.getAttribute("media");
          const { width: mediaWidth } = extractMediaDimensions(media);
          const width = parseInt(mediaWidth, 10);
          if (width < minSize) minSize = width;
        });

        // Activate all sources with that smallest size
        toArray(sources).forEach(function (e) {
          const media = e.getAttribute("media");
          const { width: mediaWidth } = extractMediaDimensions(media);
          const width = parseInt(mediaWidth, 10);
          if (width === minSize) {
            sourcesToActivate.push(e);
          }
        });
      }

      // Reverse back to DOM order so AVIF sources (which are first in HTML) get activated first
      sourcesToActivate.reverse();

      // Temporarily hide picture to prevent browser from evaluating during setAttribute
      const originalDisplay = image.style.display;
      image.style.display = "none";

      // Set all srcsets atomically while hidden
      sourcesToActivate.forEach(function (e) {
        e.setAttribute("srcset", e.getAttribute(settings.srcset));
      });

      // Set img src and srcset AFTER source elements are ready
      if (img.dataset.srcset) {
        img.setAttribute("srcset", img.dataset.srcset);
      }
      img.setAttribute("src", imgSrc);

      // Force browser to re-evaluate picture element
      image.style.display = originalDisplay;
      void image.offsetHeight; // Force reflow
    } else {
      image.style.backgroundImage = "url(" + source + ")";
    }
  }

  image.setAttribute("data-unveil", false);

  if ("objectFit" in document.documentElement.style === false) {
    Array.prototype.forEach.call(
      document.querySelectorAll("img[data-object-fit]"),
      function (imageEl) {
        (imageEl.runtimeStyle || imageEl.style).background =
          'url("' +
          imageEl.getAttribute("data-src") +
          '") no-repeat 50%/' +
          (imageEl.currentStyle
            ? imageEl.currentStyle["object-fit"]
            : imageEl.getAttribute("data-object-fit"));

        imageEl.src =
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='" +
          imageEl.width +
          "' height='" +
          imageEl.height +
          "'%3E%3C/svg%3E";
      }
    );
  }
};

export const init = () => {
  let images = [];
  initComponent(
    "unveil",
    (image) => {
      images.push(image);
    },
    '[data-unveil="true"]'
  );
  callbackWhenInView({ elems: images, callback: unveil });
};
init();
